import { ReactNode, useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router';
import { ArrowLeft, Folder, Package, FlexAlignLeft } from '@untitledui/icons';
import { useBackLink } from '@/hooks/use-back-link';
import { DocumentModeSelector, type DocumentViewMode } from './document-mode-selector';
import { useTranslation } from 'react-i18next';
import { Tooltip, TooltipTrigger } from '@/components/base/tooltip/tooltip';

interface DocumentViewerLayoutProps {
  // Mode management
  mode: DocumentViewMode;
  onModeChange: (mode: DocumentViewMode) => void;
  availableModes?: DocumentViewMode[];

  // Document context
  documentId: string;
  documentName?: string;
  containerId?: string;
  containerNumber?: string;

  // Layout slots
  sidebar?: ReactNode;
  viewer: ReactNode;
  rightPanel?: ReactNode;

  // Header customization
  headerActions?: ReactNode;
  showBackButton?: boolean;
  showQuickNavigation?: boolean;

  // Layout options
  sidebarCollapsed?: boolean;
  hideSidebar?: boolean;
  hideRightPanel?: boolean;
}

// Hook per persistenza layout preferences in localStorage
function useLayoutPreferences() {
  const STORAGE_KEY = 'document-viewer-layout-prefs';

  const [preferences, setPreferences] = useState<{
    sidebarVisible: boolean;
  }>(() => {
    if (typeof window === 'undefined') {
      return { sidebarVisible: true };
    }
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (stored) {
        const parsed = JSON.parse(stored);
        // Retrocompatibilità: rimuovi rightPanelVisible se presente
        return { sidebarVisible: parsed.sidebarVisible ?? true };
      }
    } catch {
      // Ignore parse errors
    }
    return { sidebarVisible: true };
  });

  const updatePreferences = useCallback((updates: Partial<typeof preferences>) => {
    setPreferences(prev => {
      const next = { ...prev, ...updates };
      try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
      } catch {
        // Ignore storage errors
      }
      return next;
    });
  }, []);

  return [preferences, updatePreferences] as const;
}

export function DocumentViewerLayout({
  mode,
  onModeChange,
  availableModes = ['view', 'review', 'manager'],
  documentId: _documentId,
  documentName,
  containerId,
  containerNumber,
  sidebar,
  viewer,
  rightPanel,
  headerActions,
  showBackButton = true,
  showQuickNavigation = true,
  sidebarCollapsed = false,
  hideSidebar = false,
  hideRightPanel = false,
}: DocumentViewerLayoutProps) {
  const { t } = useTranslation(['pages:documents', 'common']);
  const navigate = useNavigate();
  const backFallback = containerId ? `/shipper/containers/${containerId}?tab=documents` : '/shipper/containers';
  const { goBack } = useBackLink(backFallback);

  // Layout preferences con persistenza
  const [layoutPrefs, updateLayoutPrefs] = useLayoutPreferences();

  // Stato interno per visibilità pannelli
  // Le props hideSidebar/hideRightPanel agiscono come override iniziale
  const [sidebarVisible, setSidebarVisible] = useState(() => {
    // Se hideSidebar è true, nascondi la sidebar
    if (hideSidebar === true) return false;
    // Altrimenti usa le preferenze salvate
    return layoutPrefs.sidebarVisible;
  });

  // Right panel è sempre visibile se esiste (non ha toggle)
  const rightPanelVisible = rightPanel && !hideRightPanel;

  // Sincronizza con props quando cambiano
  useEffect(() => {
    if (hideSidebar !== undefined) {
      setSidebarVisible(!hideSidebar);
    }
  }, [hideSidebar]);

  // Salva preferenze quando cambia lo stato (solo sidebar)
  useEffect(() => {
    updateLayoutPrefs({ sidebarVisible });
  }, [sidebarVisible, updateLayoutPrefs]);

  // Handler per toggle sidebar
  const toggleSidebar = useCallback(() => {
    setSidebarVisible(prev => !prev);
  }, []);

  const handleGoToEveDrive = () => {
    // Navigate to Eve Drive - container's folder
    if (containerId) {
      navigate(`/shipper/documents?tab=myDrive&containerId=${containerId}`);
    } else {
      navigate('/shipper/documents?tab=myDrive');
    }
  };

  const handleBackToContainer = () => {
    if (containerId) {
      navigate(`/shipper/containers/${containerId}?tab=documents`);
    } else {
      navigate('/shipper/containers');
    }
  };

  // Calcola le colonne del grid in base alla visibilità
  // Right panel è sempre visibile se esiste, solo sidebar può essere nascosta
  const getGridCols = useCallback(() => {
    if (sidebar && rightPanel) {
      if (!sidebarVisible) return 'grid-cols-1 lg:grid-cols-[1fr_380px]';
      return `grid-cols-1 lg:grid-cols-[${sidebarCollapsed ? '60px' : '240px'}_1fr_380px]`;
    }
    if (sidebar) {
      if (!sidebarVisible) return 'grid-cols-1';
      return `grid-cols-1 lg:grid-cols-[${sidebarCollapsed ? '60px' : '240px'}_1fr]`;
    }
    if (rightPanel) {
      return 'grid-cols-1 lg:grid-cols-[1fr_380px]';
    }
    return 'grid-cols-1';
  }, [sidebar, rightPanel, sidebarVisible, sidebarCollapsed]);

  return (
    <div className="h-full flex flex-col">
      {/* Header — sticky when main scrolls (e.g. long doc list) */}
      <div className="sticky top-0 z-20 flex items-center justify-between px-4 py-3 border-b border-secondary bg-secondary">
        {/* LEFT: Back + Title + Container ref */}
        <div className="flex items-center gap-3 min-w-0 flex-shrink-0">
          {/* Back button */}
          {showBackButton && containerId && (
            <button
              onClick={goBack}
              className="text-tertiary hover:text-primary transition-colors flex-shrink-0"
              title={t('viewer.backToContainer', { defaultValue: 'Back to container' })}
            >
              <ArrowLeft className="size-5" />
            </button>
          )}

          {/* Title and container reference */}
          <div className="min-w-0">
            <h1 className="text-lg font-semibold text-primary truncate">
              {documentName || t('viewer.document', { defaultValue: 'Document' })}
            </h1>
            {containerNumber && (
              <button
                onClick={goBack}
                className="text-sm text-tertiary hover:text-primary transition-colors truncate"
              >
                {t('viewer.containerRef', { defaultValue: 'Container' })}: {containerNumber}
              </button>
            )}
          </div>
        </div>

        {/* CENTER: Mode selector */}
        <div className="flex-1 flex justify-center px-4">
          <DocumentModeSelector
            currentMode={mode}
            onModeChange={onModeChange}
            availableModes={availableModes}
          />
        </div>

        {/* RIGHT: Layout toggles + Quick navigation icons + custom actions */}
        <div className="flex items-center gap-2 flex-shrink-0">
          {/* Layout toggle buttons - visible only on large screens */}
          <div className="hidden lg:flex items-center gap-2 border-r border-secondary pr-2 mr-1">
            {/* Sidebar toggle */}
            {sidebar && (
              <Tooltip
                title={
                  sidebarVisible
                    ? t('viewer.hideSidebar', { defaultValue: 'Hide Sidebar' })
                    : t('viewer.showSidebar', { defaultValue: 'Show Sidebar' })
                }
                offset={2}
              >
                <TooltipTrigger onPress={toggleSidebar}>
                  <span className="p-2 rounded-lg text-tertiary hover:text-primary hover:bg-secondary transition-colors">
                    <FlexAlignLeft className="size-5" />
                  </span>
                </TooltipTrigger>
              </Tooltip>
            )}

          </div>

          {/* Quick navigation icons */}
          {showQuickNavigation && (
            <div className="flex items-center gap-2">
              {containerId && (
                <Tooltip title={t('viewer.goToContainer', { defaultValue: 'Go to Container' })} offset={2}>
                  <TooltipTrigger onPress={handleBackToContainer}>
                    <span className="p-2 rounded-lg text-tertiary hover:text-primary hover:bg-secondary transition-colors">
                      <Package className="size-5" />
                    </span>
                  </TooltipTrigger>
                </Tooltip>
              )}
              <Tooltip title={t('viewer.openEveDrive', { defaultValue: 'Open in Eve Drive' })} offset={2}>
                <TooltipTrigger onPress={handleGoToEveDrive}>
                  <span className="p-2 rounded-lg text-tertiary hover:text-primary hover:bg-secondary transition-colors">
                    <Folder className="size-5" />
                  </span>
                </TooltipTrigger>
              </Tooltip>
            </div>
          )}

          {/* Custom header actions */}
          {headerActions}
        </div>
      </div>

      {/* Content - 3 column layout */}
      <div
        className={`flex-1 grid min-h-0 overflow-hidden transition-all duration-300 ease-in-out gap-0 ${getGridCols()}`}
        style={{ gridTemplateRows: 'minmax(0, 1fr)' }}
      >
        {/* Left: Document sidebar */}
        {sidebar && sidebarVisible && (
          <div
            className={`hidden lg:flex lg:flex-col flex-shrink-0 min-h-0 overflow-y-auto ${sidebarCollapsed ? 'w-[60px]' : 'w-[240px]'
              }`}
          >
            {sidebar}
          </div>
        )}

        {/* Center: Document viewer — top-aligned, sticky below header when main scrolls */}
        <div className="relative z-10 flex flex-col min-h-0 h-full overflow-hidden sticky top-16 bg-primary">
          {viewer}
        </div>

        {/* Right: Context panel (extracted data / annotations / manager) */}
        {rightPanel && rightPanelVisible && (
          <div className="hidden lg:flex lg:flex-col border-l border-secondary min-h-0 overflow-hidden flex-shrink-0 w-[380px]">
            {rightPanel}
          </div>
        )}
      </div>
    </div>
  );
}

export default DocumentViewerLayout;
